//
// # Schema Version Registry
//
// Every schema version that has ever existed and what each was derived from.
// The registry outlives the folders: the tree carries `current/` alone, but
// every version stays recorded here.
//
// `parent` is a DAG, not a line — two versions developed in parallel are
// unordered until one merges — and it is the only thing that says how versions
// sequence, since the names do not.
//
// Only one migration edge is ever in the tree at a time, so a depot moves
// exactly one version per bump. Added by the `bump-schema-version` skill.
//

export interface SchemaVersionEntry {
  readonly id: string;
  /** `null` only for the root. */
  readonly parent: string | null;
  readonly date: string;
  readonly summary: string;
}

export const SCHEMA_HISTORY: readonly SchemaVersionEntry[] = [
  {
    id: "v7",
    parent: null,
    date: "2026-05-19",
    summary:
      "Root of the registry. Removed Schematic.symbols; symbols resolve via the depot.",
  },
  {
    id: "1046ad7af9c2",
    parent: "v7",
    date: "2026-08-25",
    summary:
      "schemaVersion becomes a string; version names become git commit short-hashes.",
  },
  {
    id: "f6a6e2d9b66b",
    parent: "1046ad7af9c2",
    date: "2026-10-13",
    summary:
      "Circuits and pins schema upgrade: schematic pin signal types; pin dir renamed direction.",
  },
] as const;

export function schemaVersionEntry(id: string): SchemaVersionEntry | undefined {
  return SCHEMA_HISTORY.find((e) => e.id === id);
}

/**
 * The chain from `id` back to the root, `id` first. `undefined` if `id` is
 * unknown or its chain is broken, so a caller cannot migrate along a partial
 * path believing it is complete.
 */
export function ancestryOf(id: string): SchemaVersionEntry[] | undefined {
  const chain: SchemaVersionEntry[] = [];
  const seen = new Set<string>();
  let current: string | null = id;
  while (current !== null) {
    if (seen.has(current)) return undefined; // cycle
    seen.add(current);
    const entry = schemaVersionEntry(current);
    if (!entry) return undefined;
    chain.push(entry);
    current = entry.parent;
  }
  return chain;
}
